import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectModulesWithTopics } from "../../store/selectors";
import {
  hideSidebar,
  toggleSidebar,
  toggleUpdateSidebar,
} from "../../store/actions";
import { useHistory } from "react-router";
import useWindowSize from "../../hooks/useWindowSize";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useTranslation } from "react-i18next";
import BadgeProgress from "../badge/BadgeProgress";
import { AnimatePresence, motion } from "framer-motion";
import { trackAdobeClick } from "../../utils/adobeLogging";

const ModulesSidebar = () => {
  const [toggles, setToggles] = useState({});
  const modules = useSelector(selectModulesWithTopics);
  const topics = useSelector((state) => state.topics);
  const section = useSelector((state) => state.progress.section);
  const finishedTopics = useSelector((state) => state.progress.finishedTopics);
  const isSidebarVisible = useSelector((state) => state.ui.sidebar);
  const currentTopic = useSelector((state) => state.ui.currentTopicContentKey);
  const updateSidebar = useSelector((state) => state.ui.updateSidebar);
  const [width] = useWindowSize();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const history = useHistory();

  const sidebarRef = useRef(null);

  useEffect(() => {
    if (updateSidebar) {
      setModuleSection(modules[1].contentKey, true);
      dispatch(toggleUpdateSidebar());
    }
  }, [updateSidebar]);

  useEffect(() => {
    const name = `${section}Toggles`;
    if (localStorage[name]) {
      setToggles(JSON.parse(localStorage[name]));
    }
    if (currentTopic && !localStorage[name]) {
      let result = {};
      modules.forEach((item) => {
        const isCurrent = item.topics
          .map((t) => t.contentKey)
          .includes(currentTopic);
        result[item.contentKey] = isCurrent;
        if (isCurrent) localStorage[`${section}Module`] = item.contentKey;
      });
      localStorage[name] = JSON.stringify(result);
      setToggles(result);
    }
  }, [modules, section, currentTopic]);

  useEffect(() => {
    if (currentTopic) {
      const topic = topics[currentTopic];
      const previousModuleKey = localStorage[`${section}Module`];
      if (previousModuleKey && previousModuleKey !== topic.parentContentId) {
        swapModuleSections(previousModuleKey, topic.parentContentId);
      }
      localStorage[`${section}Module`] = topic.parentContentId;
    }
  }, [currentTopic, topics, section]);

  useOutsideClick(sidebarRef, () => {
    if (width < 1024 && isSidebarVisible) {
      dispatch(hideSidebar());
    }
  });

  const toggleModuleSection = (contentKey) => {
    const result = {
      ...toggles,
      [contentKey]: !toggles[contentKey],
    };
    setToggles(result);
    localStorage[`${section}Toggles`] = JSON.stringify(result);
  };

  const setModuleSection = (contentKey, value) => {
    const result = {
      ...toggles,
      [contentKey]: value,
    };
    setToggles(result);
    localStorage[`${section}Toggles`] = JSON.stringify(result);
  };

  const swapModuleSections = (prev, next) => {
    const result = {
      ...toggles,
      [next]: true,
    };
    setToggles(result);
    localStorage[`${section}Toggles`] = JSON.stringify(result);
  };

  const onLinkClick = (title, moduleId, topicId, e, isIntro = false) => {
    e.preventDefault();
    if (isIntro) {
      setModuleSection(modules[1].contentKey, true);
    }
    if (isSidebarVisible) {
      dispatch(toggleSidebar());
    }
    const path = topicId ? `/app/${moduleId}/${topicId}` : `/app/${moduleId}`;

    trackAdobeClick(null, {
      linkTitle: title,
      linkModule: `${section}-modules-sidebar`,
      targetURL: path,
    });

    history.push(path);
  };

  const isTopicFinished = (topicId) => {
    return finishedTopics.includes(topicId);
  };

  const moduleProgress = (section) => {
    const finishedTopics = section.topics.reduce(
      (result, topic) =>
        isTopicFinished(topic.contentKey) ? result + 1 : result,
      0
    );
    return (finishedTopics * 100) / section.topics.length;
  };

  const isCurrentTopic = (contentKey) => {
    return contentKey === currentTopic;
  };

  const circularProgress = (percent) => (
    <svg height="15" width="15" viewBox="0 0 20 20">
      <circle r="10" cx="10" cy="10" fill="white" />
      <circle
        r="5"
        cx="10"
        cy="10"
        fill="transparent"
        stroke="#f8d448"
        strokeWidth="10"
        strokeDasharray={`calc(${percent} * 31.4 / 100) 31.4`}
        transform="rotate(-90) translate(-20)"
        style={{
          transition: "all ease 0.3s",
        }}
      />
    </svg>
  );

  return (
    <>
      <div
        ref={sidebarRef}
        className="rounded-tr-xxl bg-color-sidebar shadow-xl px-5 pt-5 overflow-scroll lg:overflow-hidden"
        adoberegion={`${section}-modules-sidebar`}
      >
        <BadgeProgress />

        <h1 className="hidden lg:block text-sm mt-5 uppercase font-bold font-secondary text-gray-900">
          {t("sidebarTitle")}
        </h1>

        <div className="text-center lg:text-left my-3">
          <a
            href="/"
            onClick={(e) =>
              onLinkClick(t("tutorial"), "activity/orientation", null, e)
            }
            className={`cursor-pointer uppercase text-sm text-blue-700 hover:text-blue-900`}
          >
            {t("tutorial")}
          </a>
        </div>

        {modules.map((module, index) => (
          <div key={module.contentKey}>
            {index === 0 ? (
              <div className="my-2">
                <a
                  href="/"
                  onClick={(e) =>
                    onLinkClick(
                      module.data.title || module.title,
                      module.contentKey,
                      null,
                      e
                    )
                  }
                  className={`bg-${module.data.accentColor} rounded-lg rounded-tr-none text-sm py-2 px-3 cursor-pointer block text-white hover:text-white uppercase`}
                >
                  <i className="text-normal fas fa-angle-right mr-3 opacity-0" />
                  {module.data.title || module.title}
                </a>
              </div>
            ) : (
              <div key={module.contentKey}>
                <div className="my-2 bg-white rounded-lg rounded-tr-none">
                  <a
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleModuleSection(module.contentKey);
                    }}
                    className={`bg-${module.data.accentColor} flex justify-between block rounded-lg rounded-tr-none text-sm py-2 px-3 cursor-pointer block text-white hover:text-white uppercase`}
                  >
                    <span>
                      <i
                        className={`text-normal fas fa-angle-${
                          toggles[module.contentKey] ? "down" : "right"
                        } mr-3`}
                      />
                      {module.data.title || module.title}
                    </span>
                    <span>{circularProgress(moduleProgress(module))}</span>
                  </a>

                  <AnimatePresence>
                    {toggles[module.contentKey] && module.data.showTopics && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="py-2 overflow-hidden"
                      >
                        {module.topics.map((topic) => (
                          <div
                            key={topic.contentKey}
                            className={`${
                              isCurrentTopic(topic.contentKey)
                                ? "text-white opacity-50 bg-" +
                                  module.data.accentColor
                                : "bg-white"
                            } flex justify-between items-center py-2 px-3`}
                          >
                            <a
                              href="/"
                              className={`${
                                isCurrentTopic(topic.contentKey)
                                  ? "text-white"
                                  : "text-gray-600"
                              } text-sm`}
                              onClick={(e) =>
                                onLinkClick(
                                  topic.data.title || topic.title,
                                  module.contentKey,
                                  topic.contentKey,
                                  e
                                )
                              }
                            >
                              {topic.data.title || topic.title}
                            </a>
                            {isTopicFinished(topic.contentKey) ? (
                              <i className="fas fa-star text-yellow-300" />
                            ) : (
                              <i className="fas fa-star text-gray-300" />
                            )}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default ModulesSidebar;
